import { c as _c } from "react/compiler-runtime";
import classNames from 'classnames';
import { forwardRef, useState, type JSX } from 'react';
import styled from 'styled-components';
import { colors } from '../../theme/colors';
import { MiniIconPerson } from '../mini-icons';
import { Text } from '../text';
export interface AvatarProps {
  imageUrl?: string;
  initials?: string;
  sizeInPixels: number;
  badgeText?: string;
  badgeColor?: string;
  disabled?: boolean;
  onClick?: () => unknown;
  isActive?: boolean;
}
interface AvatarBadgeProps {
  badgeText?: string;
  badgeColor?: string;
}
function AvatarBadge(t0) {
  const $ = _c(3);
  const {
    badgeColor: t1,
    badgeText
  } = t0;
  const badgeColor = t1 === undefined ? colors.steel[500] : t1;
  if (!badgeText) {
    return null;
  }
  let t2;
  if ($[0] !== badgeColor || $[1] !== badgeText) {
    t2 = <StyledMiniBadge className="employee-mini-badge" backgroundColor={badgeColor}>{badgeText}</StyledMiniBadge>;
    $[0] = badgeColor;
    $[1] = badgeText;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}
export const Avatar = forwardRef<HTMLDivElement, AvatarProps>((props, ref) => {
  const $ = _c(32);
  const {
    imageUrl,
    sizeInPixels,
    badgeText,
    badgeColor: t0,
    disabled: t1,
    onClick,
    isActive: t2,
    initials
  } = props;
  const badgeColor = t0 === undefined ? colors.steel[500] : t0;
  const disabled = t1 === undefined ? false : t1;
  const isActive = t2 === undefined ? false : t2;
  const [hasError, setError] = useState(false);
  const t3 = !!onClick && !disabled;
  const t4 = isActive && !disabled;
  let t5;
  if ($[0] !== t3 || $[1] !== t4) {
    t5 = classNames({
      isInteractive: t3,
      isActive: t4
    });
    $[0] = t3;
    $[1] = t4;
    $[2] = t5;
  } else {
    t5 = $[2];
  }
  const containerClass = t5;
  if (!imageUrl || hasError) {
    let t6;
    if ($[3] !== initials) {
      t6 = initials ? <Text weight={600} color="white" textAlign="center">{initials}</Text> : <MiniIconPerson color={colors.steel[200]} />;
      $[3] = initials;
      $[4] = t6;
    } else {
      t6 = $[4];
    }
    let t7;
    if ($[5] !== sizeInPixels || $[6] !== t6) {
      t7 = <StyledFallback height={sizeInPixels} width={sizeInPixels}>{t6}</StyledFallback>;
      $[5] = sizeInPixels;
      $[6] = t6;
      $[7] = t7;
    } else {
      t7 = $[7];
    }
    let t8;
    if ($[8] !== badgeColor || $[9] !== badgeText) {
      t8 = <AvatarBadge badgeColor={badgeColor} badgeText={badgeText} />;
      $[8] = badgeColor;
      $[9] = badgeText;
      $[10] = t8;
    } else {
      t8 = $[10];
    }
    let t9;
    if ($[11] !== containerClass || $[12] !== onClick || $[13] !== ref || $[14] !== sizeInPixels || $[15] !== t7 || $[16] !== t8) {
      t9 = <StyledContainer ref={ref} sizeInPixels={sizeInPixels} className={containerClass} onClick={onClick}>{t7}{t8}</StyledContainer>;
      $[11] = containerClass;
      $[12] = onClick;
      $[13] = ref;
      $[14] = sizeInPixels;
      $[15] = t7;
      $[16] = t8;
      $[17] = t9;
    } else {
      t9 = $[17];
    }
    return t9;
  }
  const t6 = `${imageUrl}`;
  let t7;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => setError(true);
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== sizeInPixels || $[20] !== t6) {
    t8 = <AvatarImage alt="photo" height={sizeInPixels} width={sizeInPixels} src={t6} onError={t7} />;
    $[19] = sizeInPixels;
    $[20] = t6;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] !== badgeColor || $[23] !== badgeText) {
    t9 = <AvatarBadge badgeColor={badgeColor} badgeText={badgeText} />;
    $[22] = badgeColor;
    $[23] = badgeText;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  if ($[25] !== containerClass || $[26] !== onClick || $[27] !== ref || $[28] !== sizeInPixels || $[29] !== t8 || $[30] !== t9) {
    t10 = <StyledContainer ref={ref} sizeInPixels={sizeInPixels} className={containerClass} onClick={onClick}>{t8}{t9}</StyledContainer>;
    $[25] = containerClass;
    $[26] = onClick;
    $[27] = ref;
    $[28] = sizeInPixels;
    $[29] = t8;
    $[30] = t9;
    $[31] = t10;
  } else {
    t10 = $[31];
  }
  return t10;
});
const AvatarImage = styled.img`
  border-radius: 50%;
  height: inherit;
  object-fit: cover;
  max-width: inherit;
`;
const StyledContainer = styled.div<{
  sizeInPixels: number;
}>`
  position: relative;
  height: ${({
  sizeInPixels
}) => sizeInPixels}px;
  width: ${({
  sizeInPixels
}) => sizeInPixels}px;
  border-radius: 50%;
  transition: 200ms box-shadow ease-in-out;

  &.isInteractive {
    cursor: pointer;
  }

  &.isInteractive:hover,
  &.isActive {
    box-shadow: 0 0 0 2px ${colors.brand[300]};
  }
`;
const StyledMiniBadge = styled.div<{
  backgroundColor: string;
}>`
  background-color: ${({
  backgroundColor
}) => backgroundColor};
  display: flex;
  flex-direction: row;
  padding: 1px 2px;
  border-radius: 2px;
  color: #fff;
  position: absolute;
  height: 10px;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  right: 0;
  bottom: 0;
  font-family: Open Sans;
  font-style: normal;
  font-weight: 800;
  font-size: 9px;
  line-height: 8px;
  text-align: center;
  text-transform: uppercase;
`;
const StyledFallback = styled.div<{
  height: number;
  width: number;
  disabled?: boolean;
}>`
  height: ${({
  height
}) => height ? `${height}px` : 'auto'};
  width: ${({
  width
}) => width ? `${width}px` : 'auto'};
  border-radius: 50%;
  background-color: ${colors.steel[300]};
  display: flex;
  align-items: center;
  justify-content: center;
`;